import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "dashboard-container" }
const _hoisted_2 = { class: "widgets-grid" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = {
  key: 0,
  class: "widget-loading"
}
const _hoisted_5 = { key: 1 }
const _hoisted_6 = {
  key: 0,
  class: "alert alert-warning mt-3"
}
const _hoisted_7 = {
  key: 0,
  class: "error-list"
}
const _hoisted_8 = {
  key: 1,
  class: "content-section"
}
const _hoisted_9 = {
  key: 0,
  class: "d-flex justify-content-center my-3"
}
const _hoisted_10 = {
  key: 0,
  class: "section-title"
}

import { ref, onMounted, computed, defineAsyncComponent } from "vue"
import { useAuthStore } from "@/stores/auth"
import { DeliveryStatus } from "@/api/deliveries"
import dashboardApi, { Counts } from "@/api/dashboard"

// Lazy load components for better initial page load

export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardView',
  setup(__props) {

const StoresView = defineAsyncComponent(() => import("@/views/StoresView.vue"))
const UsersView = defineAsyncComponent(() => import("@/views/UsersView.vue"))
const DeliveriesView = defineAsyncComponent(() => import("@/views/DeliveriesView.vue"))
const DriversView = defineAsyncComponent(() => import("@/views/DriversView.vue"))

const authStore = useAuthStore()

// Dashboard data with shallow references to avoid deep reactivity overhead
const counts = ref({
  totalStores: 0,
  totalUsers: 0,
  totalDrivers: 0,
  totalDeliveries: 0,
  deliveries: {}
} as Counts)

// Individual loading states for each widget
const loadingCounts = ref(false)

const errors = ref([])
const showDetailedErrors = ref(false)

// Section management
const selectedWidget = ref<string | null>(null)
const activeSection = ref<string | null>(null)

// Computed properties for determining which component to show
const isStatusSection = computed(() => ["PendingDelivery", "PendingUpload", "Delivered", "NotDelivered"].includes(activeSection.value || ""))

const getComponentForSection = computed(() => {
  switch (activeSection.value) {
    case "stores":
      return StoresView
    case "users":
      return UsersView
    case "drivers":
      return DriversView
    case "deliveries":
    case "PendingDelivery":
    case "PendingUpload":
    case "Delivered":
    case "NotDelivered":
      return DeliveriesView
    default:
      return null
  }
})

// Define widgets based on user role for more efficient rendering
const visibleWidgets = computed(() => {
  // const userRole = authStore.userInfo?.role;
  const widgets = []

  if (canView(["Admin"])) {
    widgets.push({ id: "stores", title: "Total Stores", count: counts.value.totalStores })
  }

  if (canView(["Admin", "Manager"])) {
    widgets.push({ id: "users", title: "Total Users", count: counts.value.totalUsers }, { id: "deliveries", title: "Total Deliveries", count: counts.value.totalDeliveries }, { id: "drivers", title: "Total Drivers", count: counts.value.totalDrivers })
  }

  if (canView(["Driver"])) {
    widgets.push(
      { id: "PendingDelivery", title: "Pending", count: counts.value.deliveries[DeliveryStatus.PendingDelivery] },
      { id: "PendingUpload", title: "Pending Upload", count: counts.value.deliveries[DeliveryStatus.PendingUpload] },
      { id: "Delivered", title: "Delivered", count: counts.value.deliveries[DeliveryStatus.Delivered] },
      { id: "NotDelivered", title: "Not Delivered", count: counts.value.deliveries[DeliveryStatus.NotDelivered] }
    )
  }

  return widgets
})

onMounted(async () => {
  fetchDashboardData()
  setInitialView()
})

const fetchDashboardData = async () => {
  // Reset error state
  errors.value = []

  loadingCounts.value = true
  counts.value = await dashboardApi.getCounts()
  loadingCounts.value = false
}

const canView = (roles: string[]): boolean => !!authStore.userInfo?.role && roles.includes(authStore.userInfo.role)

function toggleSection(section: string) {
 if (activeSection.value !== section) {
    selectedWidget.value = section
    activeSection.value = section
  }
}

function setInitialView() {
  const userRole = authStore.userInfo?.role

  if (userRole === "Driver") {
    selectedWidget.value = "PendingDelivery"
    activeSection.value = "PendingDelivery"
  } else if (userRole === "Manager") {
    selectedWidget.value = "users"
    activeSection.value = "users"
  } else {
    selectedWidget.value = "stores"
    activeSection.value = "stores"
  }
}

function getStatusValue(activeSection: string): number {
  const key = activeSection as (keyof typeof DeliveryStatus)
  return DeliveryStatus[key] || 0
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[3] || (_cache[3] = _createElementVNode("h1", null, "Dashboard", -1)),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleWidgets.value, (widget) => {
        return (_openBlock(), _createElementBlock("div", {
          key: widget.id,
          class: _normalizeClass(["widget clickable", {
          'active-widget': selectedWidget.value === widget.id,
          'loading-widget': loadingCounts.value
        }]),
          onClick: ($event: any) => (toggleSection(widget.id))
        }, [
          _createElementVNode("h6", null, _toDisplayString(widget.title), 1),
          (loadingCounts.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[0] || (_cache[0] = [
                _createElementVNode("div", { class: "spinner-border spinner-border-sm" }, null, -1)
              ]))]))
            : (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(widget.count), 1))
        ], 10, _hoisted_3))
      }), 128))
    ]),
    (errors.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _cache[1] || (_cache[1] = _createElementVNode("p", null, [
            _createElementVNode("strong", null, "Note:"),
            _createTextVNode(" Some data may be incomplete. Please refresh to try again.")
          ], -1)),
          (showDetailedErrors.value)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (error, index) => {
                  return (_openBlock(), _createElementBlock("li", { key: index }, _toDisplayString(error), 1))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (activeSection.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          (loadingCounts.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[2] || (_cache[2] = [
                _createElementVNode("div", { class: "spinner-border" }, null, -1)
              ]))]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (isStatusSection.value)
                  ? (_openBlock(), _createElementBlock("h3", _hoisted_10, "Recent " + _toDisplayString(activeSection.value.replace(/([A-Z])/g, " $1").trim()), 1))
                  : _createCommentVNode("", true),
                (_openBlock(), _createBlock(_resolveDynamicComponent(getComponentForSection.value), _mergeProps({ isDashboard: true }, isStatusSection.value ? {
    selectedStatuses: getStatusValue(activeSection.value),
    initialStatusFilter: getStatusValue(activeSection.value)
  } : {}), null, 16))
              ], 64))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})